import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'rootReducer';

import MatchEvent from 'interfaces/matchEvent';

import getStateFromThunkAPI from '../helpers/getStateFromThunkAPI';

import { matchSliceActions, selectAllPlayers } from 'features/match/matchSlice';
import {
  getConnectedAssistEvent,
  getConnectedFouledEvent,
  getConnectedOffenceEvent,
  getConnectedPenaltiesGoalEvent,
  getConnectedPenaltiesMissEvent,
  getConnectedPenaltyGoalEvent,
  getConnectedPenaltyMissEvent,
  getConnectedPenaltySaveEvent,
  getConnectedPlayerOnEvent,
} from '../../../helpers/getConnectedEvent';
import { MatchEventType } from '../../../enums/matchEventType';
import { TeamOption } from '../../../enums/teamOption';
import { Person } from '../../../interfaces/person';
import { inputSliceActions } from '../inputSlice';

const editEvent = createAsyncThunk('input/editEvent', async (matchEvent: MatchEvent, thunkAPI) => {
  const rootState = thunkAPI.getState() as RootState;
  const { matchState } = getStateFromThunkAPI(thunkAPI);
  const { dispatch } = thunkAPI;
  const { match } = matchState;

  dispatch(matchSliceActions.clearActiveMatchEvents());
  dispatch(matchSliceActions.setEventInternal({ mainEvent: true, active: true, id: matchEvent.id }));

  let matchEvent2;
  let matchEvent3;
  let matchEvent4;
  let matchEvent5;
  let matchEvent6;
  switch (matchEvent.matchEventTypeId) {
    case MatchEventType.OWN_GOAL:
    case MatchEventType.GOAL: {
      matchEvent2 = getConnectedAssistEvent(match.matchEvents, matchEvent);
      break;
    }

    case MatchEventType.PLAYER_OFF: {
      matchEvent2 = getConnectedPlayerOnEvent(match.matchEvents, matchEvent);
      break;
    }

    case MatchEventType.PENALTY: {
      matchEvent2 = getConnectedFouledEvent(match.matchEvents, matchEvent);
      matchEvent3 = getConnectedOffenceEvent(match.matchEvents, matchEvent);
      matchEvent4 = getConnectedPenaltyMissEvent(match.matchEvents, matchEvent);
      matchEvent5 = getConnectedPenaltySaveEvent(match.matchEvents, matchEvent);
      matchEvent6 = getConnectedPenaltyGoalEvent(match.matchEvents, matchEvent);
      break;
    }

    case MatchEventType.PENALTIES_PENALTY: {
      matchEvent4 = getConnectedPenaltiesMissEvent(match.matchEvents, matchEvent);
      matchEvent5 = getConnectedPenaltySaveEvent(match.matchEvents, matchEvent);
      matchEvent6 = getConnectedPenaltiesGoalEvent(match.matchEvents, matchEvent);
      break;
    }

    case MatchEventType.PENALTY_MISS:
    case MatchEventType.PENALTIES_MISS: {
      matchEvent2 = getConnectedPenaltySaveEvent(match.matchEvents, matchEvent);
    }
  }

  if (matchEvent2) {
    dispatch(matchSliceActions.setEventInternal({ mainEvent: false, active: true, id: matchEvent2.id }));
  }

  if (matchEvent3) {
    dispatch(matchSliceActions.setEventInternal({ mainEvent: false, active: true, id: matchEvent3.id }));
  }

  if (matchEvent4) {
    dispatch(matchSliceActions.setEventInternal({ mainEvent: false, active: true, id: matchEvent4.id }));
  }

  if (matchEvent5) {
    dispatch(matchSliceActions.setEventInternal({ mainEvent: false, active: true, id: matchEvent5.id }));
  }

  if (matchEvent6) {
    dispatch(matchSliceActions.setEventInternal({ mainEvent: false, active: true, id: matchEvent6.id }));
  }

  const allPlayers = selectAllPlayers(rootState);

  clearInputsAndLoadUniversalEditables();
  setActiveTeamAndEventFromEvent();

  const eventPersonId = findPersonIdFromEvent(matchEvent, allPlayers);

  switch (matchEvent.matchEventTypeId) {
    case MatchEventType.YELLOW_CARD: {
      dispatch(inputSliceActions.setSelectYellowCard(eventPersonId));
      break;
    }

    case MatchEventType.SECOND_YELLOW_CARD: {
      dispatch(inputSliceActions.setSelectYellowCard(eventPersonId));
      break;
    }

    case MatchEventType.RED_CARD: {
      dispatch(inputSliceActions.setSelectRedCard(eventPersonId));
      break;
    }

    case MatchEventType.CORNER: {
      dispatch(inputSliceActions.setSelectCorner(eventPersonId));
      break;
    }

    case MatchEventType.OFFSIDE: {
      dispatch(inputSliceActions.setSelectOffside(eventPersonId));
      break;
    }

    case MatchEventType.FREE_KICK: {
      dispatch(inputSliceActions.setSelectFreeKick(eventPersonId));
      break;
    }

    case MatchEventType.DISALLOWED_GOAL:
    case MatchEventType.GOAL: {
      editGoalEvent();
      break;
    }

    case MatchEventType.PLAYER_OFF: {
      editSubstitutionEvent();
      break;
    }

    case MatchEventType.OWN_GOAL: {
      editOwnGoalEvent();
      break;
    }

    case MatchEventType.PENALTY:
    case MatchEventType.PENALTIES_PENALTY: {
      editPenaltyEvent();
      break;
    }

    case MatchEventType.PENALTY_MISS:
    case MatchEventType.PENALTIES_MISS: {
      editPenaltyMissEvent();
      break;
    }

    case MatchEventType.PENALTIES_GOAL:
      editPenaltiesGoalEvent();
      break;
  }

  function clearInputsAndLoadUniversalEditables() {
    dispatch(inputSliceActions.newEvent());
    dispatch(inputSliceActions.editEventUniversal(matchEvent));
  }

  function setActiveTeamAndEventFromEvent() {
    let teamOption: any;

    if (matchEvent.team) {
      if (matchEvent.team.id === match.homeTeam.id) {
        teamOption = TeamOption.HOME;
      } else {
        teamOption = TeamOption.AWAY;
      }
    }

    dispatch(
      inputSliceActions.setActive({
        eventTypeId: matchEvent.matchEventTypeId,
        team: teamOption,
        eventId: matchEvent.id,
        important: matchEvent.important,
      }),
    );
  }

  function editSubstitutionEvent() {
    const playerOffPersonId = findPersonIdFromEvent(matchEvent, allPlayers);

    const connectedEvent = getConnectedPlayerOnEvent(match.matchEvents, matchEvent);

    dispatch(inputSliceActions.setSelectPlayerOff(playerOffPersonId));

    const playerOnPersonId = findPersonIdFromEvent(connectedEvent, allPlayers);
    dispatch(inputSliceActions.setSelectPlayerOn(playerOnPersonId));
  }

  function editPenaltyEvent() {
    const penaltyPersonId = findPersonIdFromEvent(matchEvent, allPlayers);

    const fouledEvent = getConnectedFouledEvent(match.matchEvents, matchEvent);
    const offenceEvent = getConnectedOffenceEvent(match.matchEvents, matchEvent);

    dispatch(inputSliceActions.setSelectPlayerPenaltyTaker(penaltyPersonId));

    ifFouledPersonSet(fouledEvent);
    ifOffencePersonSet(offenceEvent);
  }

  function editPenaltyMissEvent() {
    const penaltyPersonId = findPersonIdFromEvent(matchEvent, allPlayers);

    const penaltySaveEvent = getConnectedPenaltySaveEvent(match.matchEvents, matchEvent);

    dispatch(inputSliceActions.setSelectPlayerPenaltyTaker(penaltyPersonId));

    ifPenaltySavePersonSet(penaltySaveEvent);
  }

  function editPenaltiesGoalEvent() {
    const penaltyPersonId = findPersonIdFromEvent(matchEvent, allPlayers);
    dispatch(inputSliceActions.setSelectPlayerPenaltyTaker(penaltyPersonId));
  }

  function editOwnGoalEvent() {
    const goalPersonId = findPersonIdFromEvent(matchEvent, allPlayers);
    const connectedEvent = getConnectedAssistEvent(match.matchEvents, matchEvent);

    dispatch(inputSliceActions.setSelectGoalScorer(goalPersonId));
    dispatch(inputSliceActions.setOwnGoalCheckbox(true));

    ifAssistPersonSet(connectedEvent);

    if (connectedEvent && connectedEvent.matchEventTypeId === MatchEventType.INDIRECT_ASSIST) {
      dispatch(inputSliceActions.setIndirectAssistCheckbox(true));
    }
  }

  function editGoalEvent() {
    const goalPersonId = findPersonIdFromEvent(matchEvent, allPlayers);
    const connectedEvent = getConnectedAssistEvent(match.matchEvents, matchEvent);

    dispatch(inputSliceActions.setSelectGoalScorer(goalPersonId));
    ifAssistPersonSet(connectedEvent);

    if (connectedEvent && connectedEvent?.matchEventTypeId === MatchEventType.INDIRECT_ASSIST) {
      dispatch(inputSliceActions.setIndirectAssistCheckbox(true));
    }
  }

  function ifAssistPersonSet(connectedEvent: MatchEvent | undefined) {
    if (connectedEvent) {
      const assistPersonId = findPersonIdFromEvent(connectedEvent, allPlayers);
      dispatch(inputSliceActions.setSelectAssist(assistPersonId));
    }
  }

  function ifFouledPersonSet(connectedEvent: MatchEvent | undefined) {
    if (connectedEvent) {
      const assistPersonId = findPersonIdFromEvent(connectedEvent, allPlayers);
      dispatch(inputSliceActions.setSelectPlayerFouled(assistPersonId));
    }
  }

  function ifOffencePersonSet(connectedEvent: MatchEvent | undefined) {
    if (connectedEvent) {
      const assistPersonId = findPersonIdFromEvent(connectedEvent, allPlayers);
      dispatch(inputSliceActions.setSelectPlayerOffence(assistPersonId));
    }
  }

  function ifPenaltySavePersonSet(connectedEvent: MatchEvent | undefined) {
    if (connectedEvent) {
      const keeperPersonId = findPersonIdFromEvent(connectedEvent, allPlayers);
      dispatch(inputSliceActions.setSelectPlayerPenaltyKeeper(keeperPersonId));
    }
  }

  function findPersonIdFromEvent(matchEvent: MatchEvent | undefined, persons: Person[]): number | undefined {
    if (matchEvent) {
      const person = persons.find((person) => {
        return matchEvent.person && matchEvent.person.id === person.personId;
      });

      if (person) {
        return person.personId;
      }
    }
    return undefined;
  }
});

export default editEvent;
